/**
 * Entlohnungsart des Lohnmonats (AP-22 Teil 3).
 *
 * Ein Mitarbeiter wird nach einem Kollektivvertrag entlohnt — dann rechnet die
 * Engine wie immer — oder **pauschal**. Die Pauschale ist ein Schritt **nach**
 * der Rechnung: Die Wochen- und Periodenverteilung bleibt unberührt, und das
 * Ergebnis trägt beides, was gerechnet wurde und was davon abgegolten ist.
 *
 * Rein wie der Rest des Kerns.
 */

import type { MonatsErgebnis, TagErgebnis } from './types'

/** Was bei pauschaler Entlohnung mit den Stunden geschieht. */
export type PauschalModus =
  /** Keine Stunden: nur Fehlzeiten und Feiertage zählen, Normalstunden = Soll. */
  | 'FEHLZEITEN'
  /** Stunden werden gerechnet, sind aber mit der Pauschale abgegolten. */
  | 'KONTROLLE'
  /** Eine feste Zahl Mehr- und Überstunden je Monat ist abgegolten, der Rest zählt. */
  | 'UEBERSTUNDEN'

/** Die Entlohnungsart, wie der Server sie dem Monat mitgibt. `null` = Standard des Betriebs. */
export interface Entlohnung {
  id: string
  name: string
  art: 'KV' | 'PAUSCHAL'
  /** Nur bei `PAUSCHAL`. */
  modus?: PauschalModus | null
  /** Nur bei Modus `UEBERSTUNDEN`: abgegoltene Stunden je Monat. */
  inklusivStunden?: number | null
}

/** Mehr- und Überstunden in der Reihenfolge, in der die Pauschale sie abgilt. */
export interface Mehrstunden {
  mehrarbeitTeilzeit: number
  mehrarbeitVollzeit: number
  ueberstunden: number
  ueberstundenGewichtet: number
}

/** Was die Pauschale aus dem gerechneten Monat macht — steht am Ergebnis und friert mit ein. */
export interface EntlohnungErgebnis {
  id: string
  name: string
  art: 'KV' | 'PAUSCHAL'
  modus: PauschalModus | null
  /** Mit der Pauschale abgegoltene Mehr- und Überstunden (Summe, roh). */
  abgegolten: number
  /**
   * Was nach der Pauschale übrig bleibt und abzurechnen ist. `null` bei KV —
   * dort gilt, was die Summen sagen.
   */
  rest: (Mehrstunden & { zaSaldo: number }) | null
}

function rund(n: number): number {
  return Math.round(n * 100) / 100
}

/**
 * Überstundenpauschale: abgezogen wird das Billigste zuerst — Mehrarbeit
 * Teilzeit, dann Mehrarbeit 38,5–40 h, dann Überstunden. Die gewichteten
 * Überstunden sinken im selben Verhältnis wie die rohen.
 */
export function nachPauschale(m: Mehrstunden, inklusiv: number): { rest: Mehrstunden; abgegolten: number } {
  let offen = Math.max(0, inklusiv)
  const nimm = (wert: number) => {
    const ab = Math.min(Math.max(0, wert), offen)
    offen -= ab
    return rund(wert - ab)
  }
  const mehrarbeitTeilzeit = nimm(m.mehrarbeitTeilzeit)
  const mehrarbeitVollzeit = nimm(m.mehrarbeitVollzeit)
  const ueberstunden = nimm(m.ueberstunden)
  const anteil = m.ueberstunden > 0 ? ueberstunden / m.ueberstunden : 0
  return {
    rest: { mehrarbeitTeilzeit, mehrarbeitVollzeit, ueberstunden, ueberstundenGewichtet: rund(m.ueberstundenGewichtet * anteil) },
    abgegolten: rund(Math.max(0, inklusiv) - offen),
  }
}

/** Modus „Nur Fehlzeiten": ein Tag ohne Stundenabrechnung — Soll erfüllt, nichts darüber. */
function tagOhneStunden(t: TagErgebnis): TagErgebnis {
  return {
    ...t,
    gesamt: 0,
    pausenabzug: 0,
    naz: 0,
    reisezeit: 0,
    // Ein Fehlzeit-Tag behält, was die Art als Soll gutschreibt; jeder andere gilt als geleistet.
    normalStunden: t.abwesenheitKuerzel ? t.normalStunden : t.soll,
    mehrarbeitTeilzeit: 0,
    mehrarbeitVollzeit: 0,
    ueberstunden: 0,
    ueberstundenGewichtet: 0,
    zuschlagStunden: 0,
    zuschlagGewichtet: 0,
    wegzeitAusserhalb: 0,
    zaTag: 0,
    montagezeit: 0,
    montagezulageBetrag: 0,
    entfernungStufe: null,
    entfernungBetrag: 0,
    zuschlaege: [],
    minderstunden: 0,
    zaKonsumiert: 0,
    terminStunden: 0,
  }
}

function ohneStunden(e: MonatsErgebnis): MonatsErgebnis {
  const tage = e.tage.map(tagOhneStunden)
  return {
    ...e,
    tage,
    summen: {
      ...e.summen,
      gesamt: 0,
      naz: 0,
      geleistet: 0,
      normalStunden: rund(tage.reduce((s, t) => s + t.normalStunden, 0)),
      mehrarbeitTeilzeit: 0,
      mehrarbeitVollzeit: 0,
      ueberstunden: 0,
      ueberstundenGewichtet: 0,
      zuschlagStunden: 0,
      zuschlagGewichtet: 0,
      wegzeitAusserhalb: 0,
      zaSaldo: 0,
      zaKonsumiert: 0,
      reisezeit: 0,
      minderstunden: 0,
      km: 0,
      montagezeit: 0,
      terminStunden: 0,
      zuschlagJeSatz: {},
    },
    zaehler: {
      ...e.zaehler,
      lenkerTage: 0,
      montagezulageTage: 0,
      montagezulageBetrag: 0,
      entfernungszulageTage: 0,
      entfernungszulageBetrag: 0,
      entfernungTage: { klein: 0, mittel: 0, gross: 0 } as MonatsErgebnis['zaehler']['entfernungTage'],
      naechtigungen: 0,
    },
  }
}

/**
 * Wendet die Entlohnungsart auf den gerechneten Monat an. Ohne Entlohnungsart
 * kommt das Ergebnis **unverändert** zurück — auch ohne das Feld `entlohnung`,
 * damit ein Betrieb mit einem Kollektivvertrag Byte für Byte rechnet wie zuvor.
 */
export function mitEntlohnung(e: MonatsErgebnis, entlohnung: Entlohnung | null | undefined): MonatsErgebnis {
  if (!entlohnung) return e
  const kopf = { id: entlohnung.id, name: entlohnung.name, art: entlohnung.art }
  if (entlohnung.art !== 'PAUSCHAL' || !entlohnung.modus) {
    return { ...e, entlohnung: { ...kopf, modus: null, abgegolten: 0, rest: null } }
  }
  const mehr: Mehrstunden = {
    mehrarbeitTeilzeit: e.summen.mehrarbeitTeilzeit,
    mehrarbeitVollzeit: e.summen.mehrarbeitVollzeit,
    ueberstunden: e.summen.ueberstunden,
    ueberstundenGewichtet: e.summen.ueberstundenGewichtet,
  }
  const null4: Mehrstunden = { mehrarbeitTeilzeit: 0, mehrarbeitVollzeit: 0, ueberstunden: 0, ueberstundenGewichtet: 0 }
  const alles = rund(mehr.mehrarbeitTeilzeit + mehr.mehrarbeitVollzeit + mehr.ueberstunden)

  switch (entlohnung.modus) {
    case 'FEHLZEITEN': {
      const ohne = ohneStunden(e)
      return { ...ohne, entlohnung: { ...kopf, modus: 'FEHLZEITEN', abgegolten: 0, rest: { ...null4, zaSaldo: 0 } } }
    }
    case 'KONTROLLE':
      return { ...e, entlohnung: { ...kopf, modus: 'KONTROLLE', abgegolten: alles, rest: { ...null4, zaSaldo: 0 } } }
    case 'UEBERSTUNDEN': {
      const { rest, abgegolten } = nachPauschale(mehr, entlohnung.inklusivStunden ?? 0)
      // Abgegoltene Stunden wandern nicht aufs ZA-Konto; ein Minus bleibt ein Minus.
      const zaSaldo = e.summen.zaSaldo > 0 ? rund(Math.max(0, e.summen.zaSaldo - abgegolten)) : e.summen.zaSaldo
      return { ...e, entlohnung: { ...kopf, modus: 'UEBERSTUNDEN', abgegolten, rest: { ...rest, zaSaldo } } }
    }
  }
}
